/**
 * Phone Number Validation Utilities for Business Forms
 */

/**
 * Formats a phone number as user types
 * Accepts: raw input with any characters
 * Returns: formatted as (XXX) XXX-XXXX for US numbers
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // If starts with 1, remove it for US formatting
  const cleanDigits = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

  // Limit to 10 digits
  const limitedDigits = cleanDigits.slice(0, 10);

  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
};

/**
 * Validates that a phone number has exactly 10 digits
 * Accepts: formatted or unformatted phone number
 * Returns: true if valid, false otherwise
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10;
};

/**
 * Validates US phone number (with optional +1 prefix)
 * More lenient validation for API submission
 */
export const validatePhoneNumberLenient = (phone) => {
  if (!phone) return false;
  const digitsOnly = phone.replace(/\D/g, '');

  // Accept 10 digits or 11 digits starting with 1
  return digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'));
};

/**
 * Strips phone number to digits only for API submission
 * Optionally adds +1 prefix for US numbers
 */
export const formatPhoneForAPI = (phone, addCountryCode = false) => {
  const digitsOnly = phone.replace(/\D/g, '');

  // Remove leading 1 if present
  const cleanDigits = digitsOnly.startsWith('1') && digitsOnly.length === 11
    ? digitsOnly.slice(1)
    : digitsOnly;

  if (addCountryCode) {
    return `+1${cleanDigits}`;
  }

  return cleanDigits;
};

/**
 * Get phone validation error message
 */
export const getPhoneErrorMessage = (phone) => {
  if (!phone || phone.trim() === '') {
    return 'Phone number is required';
  }

  const digitsOnly = phone.replace(/\D/g, '');

  if (digitsOnly.length === 0) {
    return 'Please enter a valid phone number';
  }

  if (digitsOnly.length < 10) {
    return `Phone number must be 10 digits (${digitsOnly.length}/10)`;
  }

  if (digitsOnly.length > 10 && !(digitsOnly.length === 11 && digitsOnly.startsWith('1'))) {
    return 'Phone number must be exactly 10 digits';
  }

  return null;
};
